import { useState } from 'react';
import { X, Copy, Check, Loader2, Mail } from 'lucide-react';
import { ApiError } from '../lib/api';
import { LANGS, type NewsletterDto, type LangCode } from '@shared/types';

interface Props {
  itemCount: number;
  loading: boolean;
  error: Error | null;
  data: NewsletterDto | null;
  onClose: () => void;
}

export default function NewsletterModal({ itemCount, loading, error, data, onClose }: Props) {
  const [lang, setLang] = useState<LangCode>('cs');
  const [copied, setCopied] = useState(false);

  const bodyFor = (l: LangCode): string | null => {
    if (!data) return null;
    if (l === 'cs') return data.bodyCs;
    if (l === 'en') return data.bodyEn;
    if (l === 'pl') return data.bodyPl;
    return data.bodyVi;
  };

  const body = bodyFor(lang);

  const onCopy = async () => {
    if (!body) return;
    await navigator.clipboard.writeText(body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-30 flex items-start justify-center bg-black/40 p-4 sm:p-8">
      <div className="card w-full max-w-3xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-teal" />
            <h2 className="font-heading text-lg font-semibold text-dark">
              Newsletter z {itemCount} {itemCount === 1 ? 'zprávy' : itemCount < 5 ? 'zpráv' : 'zpráv'}
            </h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-3 px-5 py-12 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Generuju newsletter ve 4 jazycích (Sonnet, ~20–40 s)…
          </div>
        )}

        {error && (
          <div className="px-5 py-5">
            <div className="rounded-md border border-danger/40 bg-danger/10 p-4 text-sm text-danger">
              <p className="mb-1 font-semibold">
                {error instanceof ApiError ? error.code : 'Chyba'}
              </p>
              <p>{error.message}</p>
            </div>
          </div>
        )}

        {data && (
          <>
            <div className="flex border-b border-gray-200 bg-gray-50">
              {LANGS.map((l) => {
                const has = bodyFor(l.code) !== null;
                const active = lang === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    disabled={!has}
                    className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold ${
                      active
                        ? 'border-b-2 border-teal text-teal'
                        : has
                          ? 'text-gray-500 hover:text-dark'
                          : 'text-gray-300 cursor-not-allowed'
                    }`}
                    title={has ? l.label : `${l.label} se nepodařilo vygenerovat`}
                  >
                    <span aria-hidden>{l.flag}</span>
                    <span className="hidden sm:inline">{l.label}</span>
                    <span className="inline sm:hidden text-xs uppercase">{l.code}</span>
                  </button>
                );
              })}
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-5">
              {body ? (
                <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-gray-700">
                  {body}
                </pre>
              ) : (
                <div className="text-sm text-gray-500">
                  Verze v jazyce {LANGS.find((l) => l.code === lang)?.label} není k dispozici.
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-5 py-3">
              <span className="text-xs text-gray-500">
                {data.cached
                  ? '⚡ Z cache'
                  : `✨ Vygenerováno ${new Date(data.generatedAt * 1000).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`}
              </span>
              <button onClick={onCopy} disabled={!body} className="btn-primary text-xs">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Zkopírováno' : `Kopírovat ${lang.toUpperCase()}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
